/**
 * Payments (design §6) — 3 KPI cards → tabs (All/Received/Made/Advances) →
 * table (Payment # · Contact · Date · Mode · Reference · TDS · Amount).
 * Live from listPayments(); loading then empty state until payments exist.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useBaseCurrency } from '../context/currencyContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import { fmtMoney, initials, money } from '../utils/format';
import SkIcon from '../components/skIcon';
import { showToast } from '../components/skToast';
import ModalShell from '../components/modals/modalShell';
import DateField from '../components/dateField';
import DropdownSelect from '../components/dropdownSelect';
import { listPayments, createPayment } from '../services/billing/payment.service';
import { listContacts } from '../services/billing/contact.service';
import { listDocuments } from '../services/billing/document.service';
import { BillingDocument } from '../schema/billing/document';
import { PaymentMode } from '../schema/billing/common';

interface Pmt {
  number: string; contact: string; date: string; mode: string; ref: string; amount: number;
  kind: 'in' | 'out'; advance: boolean; applied: string;
  // Raw fields kept for the detail card.
  id: string; contactId: string; currency: string; unallocated: number;
  gatewayId: string | null; isRefund: boolean; createdAt: string; direction: 'RECEIVED' | 'MADE';
}

const MODE_LABEL: Record<PaymentMode, string> = {
  BANK_TRANSFER: 'Bank transfer',
  RAZORPAY: 'Razorpay',
  CASH: 'Cash',
  CHEQUE: 'Cheque',
  OTHER: 'Other',
};

const TABS = [
  { id: 'all', label: 'All', f: () => true },
  { id: 'in', label: 'Received', f: (p: Pmt) => p.kind === 'in' },
  { id: 'out', label: 'Made', f: (p: Pmt) => p.kind === 'out' },
  { id: 'adv', label: 'Advances', f: (p: Pmt) => p.advance },
];

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});
// Primary CTA — indigo→violet, matching the document lists' "New" action.
const CTA_A = '#6366f1';
const CTA_B = '#8b5cf6';
// Per-contact gradient avatars — consistent for the same name.
const AV: Array<[string, string]> = [
  ['#f97316', '#db2777'], ['#3b82f6', '#6366f1'], ['#10b981', '#059669'],
  ['#f59e0b', '#ea580c'], ['#8b5cf6', '#6d28d9'], ['#06b6d4', '#0891b2'],
];
const avatarOf = (name: string): [string, string] => {
  let h = 0;
  const n = name || '?';
  for (let i = 0; i < n.length; i++) h = (h + n.charCodeAt(i)) % AV.length;
  return AV[h];
};

type Col = (key: string, align?: 'left' | 'right' | 'center') => any;

/** Interactive KPI card — hover-lifts and filters the list on press. */
const StatCard: React.FC<{ label: string; value: string; note: string; icon: string; ink: string; soft: string; line: string; active: boolean; onPress: () => void }> = ({ label, value, note, icon, ink, soft, line, active, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1, minWidth: 170, flexDirection: 'row', alignItems: 'center', gap: 13, paddingVertical: 14, paddingHorizontal: 16,
        borderRadius: 14, borderWidth: 1, borderColor: active ? line : hover ? colors.lineStrong : colors.line,
        backgroundColor: active ? soft : colors.card, ...theme.shadow(hover ? 'md' : 'sm'),
        ...web({ transition: 'transform .16s ease, box-shadow .16s ease, border-color .16s ease', transform: hover ? 'translateY(-2px)' : 'translateY(0)', cursor: 'pointer', ...(active ? { backgroundImage: `linear-gradient(145deg, ${soft}, ${colors.card})` } : {}) }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: soft, borderWidth: 1, borderColor: line, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${soft}, ${colors.card})`, boxShadow: `0 4px 10px -3px ${ink}40` }) }}>
        <SkIcon name={icon} size={18} color={ink} strokeWidth={2.1} />
      </View>
      <View style={{ minWidth: 0, flex: 1 }}>
        <Text style={{ fontFamily: font.sansMedium, fontSize: 11.5, fontWeight: '500', color: colors.ink3 }}>{label}</Text>
        <Text numberOfLines={1} style={{ fontFamily: font.monoSemibold, fontSize: 20, fontWeight: '600', color: colors.ink, letterSpacing: -0.4, marginTop: 2 }}>{value}</Text>
        <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, marginTop: 2 }}>{note}</Text>
      </View>
    </Pressable>
  );
};

/** Hover-reactive desktop payment row with staggered entrance. */
const PaymentRow: React.FC<{ p: Pmt; index: number; col: Col; onOpen: () => void }> = ({ p, index, col, onOpen }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(a, { toValue: 1, duration: 360, delay: Math.min(index, 12) * 42, easing: Easing.out(Easing.cubic), useNativeDriver: !isWeb }).start();
  }, [a, index]);
  const known = !!p.contact && p.contact !== '—';
  const [g1, g2] = avatarOf(p.contact);
  const edge = p.advance ? colors.warn : hover ? colors.accent : 'transparent';
  return (
    <Animated.View style={{ opacity: a, transform: [{ translateY: a.interpolate({ inputRange: [0, 1], outputRange: [9, 0] }) }] }}>
      <Pressable
        onPress={onOpen}
        style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: hover ? colors.paper2 : colors.card, ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }) }}
        {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
      >
        <View style={[col('num'), { borderLeftWidth: 3, borderLeftColor: edge, ...web({ transition: 'border-color .12s ease' }) }]}>
          <Text style={{ fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '600', color: colors.ink }}>{p.number}</Text>
        </View>
        <View style={col('contact')}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            {known ? (
              <View style={{ width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: g2, ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 2px 6px -2px ${g2}66` }) }}>
                <Text style={{ fontSize: 10, fontWeight: '700', color: '#fff', fontFamily: font.sansBold }}>{initials(p.contact)}</Text>
              </View>
            ) : (
              <View style={{ width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper3, borderWidth: 1, borderColor: colors.line }}>
                <SkIcon name="building" size={13} color={colors.ink4} strokeWidth={2} />
              </View>
            )}
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: known ? colors.ink : colors.ink4 }}>{known ? p.contact : 'No contact'}</Text>
              <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 11, color: p.advance ? colors.warn : colors.ink4 }}>{p.applied}</Text>
            </View>
          </View>
        </View>
        <View style={col('date', 'center')}><Text style={{ fontFamily: font.mono, fontSize: 13, color: colors.ink2 }}>{p.date}</Text></View>
        <View style={col('mode', 'center')}>
          <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 11, color: colors.ink2 }}>{p.mode}</Text>
          </View>
        </View>
        <View style={col('ref', 'center')}><Text numberOfLines={1} style={{ fontFamily: font.mono, fontSize: 12, color: colors.ink3 }}>{p.ref}</Text></View>
        <View style={col('amount', 'right')}>
          <Text style={{ fontFamily: font.monoSemibold, fontSize: 14, fontWeight: '600', letterSpacing: -0.2, color: p.kind === 'out' ? colors.ink2 : colors.success }}>{(p.kind === 'out' ? '−' : '+') + fmtMoney(p.amount, cur)}</Text>
        </View>
      </Pressable>
    </Animated.View>
  );
};

/** Mobile stacked card — no horizontal scroll. */
const PaymentCard: React.FC<{ p: Pmt; index: number; onOpen: () => void }> = ({ p, index, onOpen }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(a, { toValue: 1, duration: 360, delay: Math.min(index, 12) * 42, easing: Easing.out(Easing.cubic), useNativeDriver: !isWeb }).start();
  }, [a, index]);
  const known = !!p.contact && p.contact !== '—';
  const [g1, g2] = avatarOf(p.contact);
  return (
    <Animated.View style={{ opacity: a, transform: [{ translateY: a.interpolate({ inputRange: [0, 1], outputRange: [9, 0] }) }] }}>
      <Pressable onPress={onOpen} style={{ borderBottomWidth: 1, borderBottomColor: colors.line, borderLeftWidth: 3, borderLeftColor: p.advance ? colors.warn : 'transparent', paddingVertical: 14, paddingHorizontal: 16, backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Text style={{ fontFamily: font.monoSemibold, fontSize: 13, fontWeight: '600', color: colors.ink }}>{p.number}</Text>
          <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 11, color: colors.ink2 }}>{p.mode}</Text>
          </View>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          {known ? (
            <View style={{ width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: g2, ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 2px 6px -2px ${g2}66` }) }}>
              <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff', fontFamily: font.sansBold }}>{initials(p.contact)}</Text>
            </View>
          ) : (
            <View style={{ width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper3, borderWidth: 1, borderColor: colors.line }}>
              <SkIcon name="building" size={14} color={colors.ink4} strokeWidth={2} />
            </View>
          )}
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 14, fontWeight: '500', color: known ? colors.ink : colors.ink4 }}>{known ? p.contact : 'No contact'}</Text>
            <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 11, color: p.advance ? colors.warn : colors.ink4 }}>{p.applied}</Text>
          </View>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <View>
            <Text style={{ fontFamily: font.sans, fontSize: 9.5, color: colors.ink4, fontWeight: '500', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date · Ref</Text>
            <Text style={{ fontFamily: font.mono, fontSize: 12.5, color: colors.ink2, marginTop: 2 }}>{p.date}{p.ref && p.ref !== '—' ? `  ·  ${p.ref}` : ''}</Text>
          </View>
          <Text style={{ fontFamily: font.monoSemibold, fontSize: 16, fontWeight: '600', letterSpacing: -0.3, color: p.kind === 'out' ? colors.ink2 : colors.success }}>{(p.kind === 'out' ? '−' : '+') + fmtMoney(p.amount, cur)}</Text>
        </View>
      </Pressable>
    </Animated.View>
  );
};

/* ── Premium payment detail card ─────────────────────────────────────── */
const DetailField: React.FC<{ icon: string; label: string; value?: string | null; mono?: boolean; copyable?: boolean; flex?: number }> = ({ icon, label, value, mono, copyable, flex }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  const has = !!(value && String(value).trim());
  const copy = () => {
    if (!has || !isWeb) return;
    const nav = (globalThis as any).navigator;
    nav?.clipboard?.writeText?.(String(value)).then(() => showToast('success', 'Copied', `${label} copied to clipboard`)).catch(() => {});
  };
  return (
    <Pressable
      onPress={copyable && has ? copy : undefined}
      style={{ flex, flexDirection: 'row', alignItems: 'center', gap: 11, padding: 11, borderRadius: 11, borderWidth: 1, borderColor: colors.line, backgroundColor: hover && copyable && has ? colors.paper2 : colors.paper, ...web(copyable && has ? { transition: 'background-color .12s ease', cursor: 'pointer' } : {}) }}
      {...(isWeb && copyable && has ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ width: 32, height: 32, borderRadius: 9, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
        <SkIcon name={icon} size={15} color={colors.accentInk} strokeWidth={2} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</Text>
        <Text numberOfLines={1} style={{ fontFamily: has ? (mono ? font.monoMedium : font.sansMedium) : font.sans, fontSize: 13.5, fontWeight: '500', color: has ? colors.ink : colors.ink4, marginTop: 2 }}>{has ? value : '—'}</Text>
      </View>
      {copyable && has && isWeb && (
        <View style={{ opacity: hover ? 1 : 0, ...web({ transition: 'opacity .12s ease' }) }}>
          <SkIcon name="link" size={13} color={colors.ink4} strokeWidth={2} />
        </View>
      )}
    </Pressable>
  );
};

const fmtLongDate = (s: string): string => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PaymentDetailModal: React.FC<{ visible: boolean; p: Pmt | null; onClose: () => void }> = ({ visible, p, onClose }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  if (!p) return null;
  const isOut = p.kind === 'out';
  const known = !!p.contact && p.contact !== '—';
  const [g1, g2] = avatarOf(p.contact);
  const dirTone = isOut
    ? { soft: colors.neutralSoft, ink: colors.neutral, line: colors.neutralLine, label: 'Paid out' }
    : { soft: colors.successSoft, ink: colors.success, line: colors.successLine, label: 'Received' };
  const amountColor = isOut ? colors.ink : colors.success;

  const sectionLabel = (t: string) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 9 }}>
      <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.accent }} />
      <Text style={{ fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t}</Text>
    </View>
  );

  return (
    <ModalShell visible={visible} onClose={onClose} title="Payment details" subtitle="Recorded in SK Billing" width={460} headerTone="accent" compact
      footer={
        <Pressable onPress={onClose} style={{ height: 38, paddingHorizontal: 18, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...(isWeb ? ({ cursor: 'pointer' } as any) : {}) }}>
          <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Close</Text>
        </Pressable>
      }
    >
      <View style={{ gap: 14 }}>
        {/* Hero — amount + direction */}
        <View style={{ alignItems: 'center', paddingVertical: 6 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <View style={{ paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, backgroundColor: dirTone.soft, borderWidth: 1, borderColor: dirTone.line, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <SkIcon name={isOut ? 'send' : 'wallet'} size={12} color={dirTone.ink} strokeWidth={2.2} />
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', letterSpacing: 0.3, color: dirTone.ink }}>{dirTone.label}</Text>
            </View>
            {p.advance && (
              <View style={{ paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, backgroundColor: colors.warnSoft, borderWidth: 1, borderColor: colors.warnLine }}>
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', letterSpacing: 0.3, color: colors.warn }}>Advance</Text>
              </View>
            )}
            {p.isRefund && (
              <View style={{ paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, backgroundColor: colors.dangerSoft, borderWidth: 1, borderColor: colors.dangerLine }}>
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', letterSpacing: 0.3, color: colors.danger }}>Refund</Text>
              </View>
            )}
          </View>
          <Text style={{ fontFamily: font.monoSemibold, fontSize: 32, fontWeight: '700', color: amountColor, letterSpacing: -0.6 }}>{(isOut ? '−' : '+') + fmtMoney(p.amount, cur)}</Text>
          <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4, marginTop: 4 }}>{p.currency} · {p.mode}</Text>
        </View>

        {/* Contact card */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper }}>
          {known ? (
            <View style={{ width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: g2, ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 4px 12px -3px ${g2}88` }) }}>
              <Text style={{ fontFamily: font.sansBold, fontSize: 13, fontWeight: '700', color: '#fff' }}>{initials(p.contact)}</Text>
            </View>
          ) : (
            <View style={{ width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper3, borderWidth: 1, borderColor: colors.line }}>
              <SkIcon name="building" size={17} color={colors.ink4} strokeWidth={2} />
            </View>
          )}
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.7 }}>{isOut ? 'Vendor' : 'Customer'}</Text>
            <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 14, fontWeight: '500', color: known ? colors.ink : colors.ink4, marginTop: 2 }}>{known ? p.contact : 'No contact on record'}</Text>
            <Text style={{ fontFamily: font.sans, fontSize: 11, color: p.advance ? colors.warn : colors.ink4, marginTop: 1 }}>{p.applied}{p.advance ? ` · ${fmtMoney(p.unallocated, cur)} unapplied` : ''}</Text>
          </View>
        </View>

        <View style={{ height: 1, backgroundColor: colors.line }} />

        {/* Details */}
        <View>
          {sectionLabel('Transaction')}
          <View style={{ gap: 9 }}>
            <View style={{ flexDirection: 'row', gap: 9 }}>
              <DetailField icon="receipt" label="Payment ID" value={p.id} mono copyable flex={1} />
              <DetailField icon="calendar" label="Payment date" value={fmtLongDate(p.date)} flex={1} />
            </View>
            <View style={{ flexDirection: 'row', gap: 9 }}>
              <DetailField icon="bank" label="Mode" value={p.mode} flex={1} />
              <DetailField icon="link" label="Reference" value={p.ref && p.ref !== '—' ? p.ref : ''} mono copyable flex={1} />
            </View>
            {!!p.gatewayId && <DetailField icon="rupee" label="Gateway payment ID" value={p.gatewayId} mono copyable />}
            {!!p.createdAt && <DetailField icon="clock" label="Recorded on" value={fmtLongDate(p.createdAt)} />}
          </View>
        </View>
      </View>
    </ModalShell>
  );
};

const Payments: React.FC = () => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const { isWide, isMobile } = useResponsive();
  const [tab, setTab] = useState('all');
  const [payments, setPayments] = useState<Pmt[]>([]);
  const [loading, setLoading] = useState(true);
  const [recordOpen, setRecordOpen] = useState(false);
  const [sel, setSel] = useState<Pmt | null>(null);
  const [contactNames, setContactNames] = useState<Record<string, string>>({});

  // contact_id → name map, so payments show the real customer/vendor name
  // instead of the raw CRM contact id the payments API returns.
  useEffect(() => {
    let alive = true;
    listContacts()
      .then((list) => { if (alive) setContactNames(Object.fromEntries((list || []).map((c) => [c.id, c.name]))); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    return listPayments()
      .then((rows: any[]) => {
        setPayments(
          (Array.isArray(rows) ? rows : []).map((p) => {
            const unalloc = money(p.unallocated_amount);
            return {
              number: (p.id || '').slice(0, 8) || 'PMT',
              contact: p.contact_name || p.contact_id || '—',
              date: p.payment_date || '',
              mode: MODE_LABEL[p.mode as PaymentMode] || p.mode || '—',
              ref: p.reference || '—',
              amount: money(p.amount),
              kind: p.direction === 'MADE' ? 'out' : 'in',
              advance: p.direction === 'RECEIVED' && unalloc > 0,
              applied: unalloc > 0 ? 'Unapplied → advance' : 'Allocated',
              id: p.id || '',
              contactId: p.contact_id || '',
              currency: p.currency || 'INR',
              unallocated: unalloc,
              gatewayId: p.gateway_payment_id || null,
              isRefund: !!p.is_refund,
              createdAt: p.created_at || '',
              direction: p.direction === 'MADE' ? 'MADE' : 'RECEIVED',
            } as Pmt;
          }),
        );
      })
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const [query, setQuery] = useState('');
  const [newHover, setNewHover] = useState(false);

  // Overlay resolved contact names; everything downstream (list, search, detail) uses these.
  const named = useMemo(
    () => payments.map((p) => ({ ...p, contact: contactNames[p.contactId] || p.contact })),
    [payments, contactNames],
  );

  const recIn = payments.filter((p) => p.kind === 'in').reduce((s, p) => s + p.amount, 0);
  const recOut = payments.filter((p) => p.kind === 'out').reduce((s, p) => s + p.amount, 0);
  const advances = payments.filter((p) => p.advance);
  const advSum = advances.reduce((s, p) => s + p.amount, 0);
  const kpis = [
    { label: 'Received', value: fmtMoney(recIn, cur), note: `${payments.filter((p) => p.kind === 'in').length} receipts`, icon: 'wallet', ink: colors.success, soft: colors.successSoft, line: colors.successLine, tab: 'in' },
    { label: 'Paid out', value: fmtMoney(recOut, cur), note: `${payments.filter((p) => p.kind === 'out').length} payments out`, icon: 'send', ink: colors.neutral, soft: colors.neutralSoft, line: colors.neutralLine, tab: 'out' },
    { label: 'Unapplied advances', value: fmtMoney(advSum, cur), note: `${advances.length} on account`, icon: 'clock', ink: colors.warn, soft: colors.warnSoft, line: colors.warnLine, tab: 'adv' },
  ];

  const rows = useMemo(() => {
    let r = named.filter((TABS.find((t) => t.id === tab) || TABS[0]).f);
    const q = query.trim().toLowerCase();
    if (q) r = r.filter((p) => (p.contact + ' ' + p.number + ' ' + p.ref + ' ' + p.mode).toLowerCase().includes(q));
    return r;
  }, [tab, named, query]);

  // Column config — fixed widths on mobile (horizontal scroll), flex weights on desktop.
  const colCfg: Record<string, { w: number; flex: number }> = {
    num: { w: 130, flex: 1.3 }, contact: { w: 220, flex: 2.6 }, date: { w: 120, flex: 1.1 },
    mode: { w: 130, flex: 1.2 }, ref: { w: 140, flex: 1.3 }, amount: { w: 130, flex: 1.2 },
  };
  const col: Col = (key, align = 'left') => {
    const c = colCfg[key];
    const center = align === 'center';
    const base = { paddingHorizontal: 16, paddingVertical: 13, justifyContent: 'center' as const, alignItems: center ? ('center' as const) : align === 'right' ? ('flex-end' as const) : ('flex-start' as const) };
    return isMobile ? { ...base, width: c.w } : { ...base, flex: c.flex };
  };
  const th = { fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600' as const, color: colors.ink4, letterSpacing: 0.6, textTransform: 'uppercase' as const };

  const footChip = (label: string, accent = false) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 5, paddingHorizontal: 11, borderRadius: 999, borderWidth: 1, borderColor: accent ? colors.accentLine : colors.line, backgroundColor: accent ? colors.accentSoft : colors.card }}>
      {accent && <SkIcon name="rupee" size={12} color={colors.accentInk} strokeWidth={2.2} />}
      <Text style={{ fontFamily: accent ? font.sansSemibold : font.sansMedium, fontSize: 11.5, fontWeight: accent ? '600' : '500', color: accent ? colors.accentInk : colors.ink3 }}>{label}</Text>
    </View>
  );

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      {/* KPI stat strip — tap to filter */}
      <View style={{ flexDirection: 'row', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        {kpis.map((k) => (
          <StatCard key={k.label} label={k.label} value={loading ? '—' : k.value} note={k.note} icon={k.icon} ink={k.ink} soft={k.soft} line={k.line} active={tab === k.tab} onPress={() => setTab(tab === k.tab ? 'all' : k.tab)} />
        ))}
      </View>

      {/* Search + Record payment */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <View style={{ width: 300, maxWidth: '55%', position: 'relative' }}>
          <View style={{ position: 'absolute', left: 11, top: 0, bottom: 0, justifyContent: 'center', zIndex: 1 }}>
            <SkIcon name="search" size={14} color={colors.ink4} strokeWidth={2} />
          </View>
          <TextInput value={query} onChangeText={setQuery} placeholder="Search this list…" placeholderTextColor={colors.ink4} style={{ height: 36, paddingLeft: 34, paddingRight: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: 9, fontSize: 12.5, color: colors.ink, fontFamily: font.sans, ...theme.shadow('sm'), ...web({ outlineStyle: 'none' }) }} />
        </View>
        <View style={{ flex: 1 }} />
        <Pressable
          onPress={() => setRecordOpen(true)}
          style={{ height: 40, paddingHorizontal: 16, borderRadius: 10, backgroundColor: CTA_A, flexDirection: 'row', alignItems: 'center', gap: 7, ...web({ backgroundImage: `linear-gradient(145deg, ${CTA_A}, ${CTA_B})`, boxShadow: newHover ? `0 10px 22px -4px ${CTA_B}aa` : `0 5px 12px -3px ${CTA_B}77`, transform: newHover ? 'translateY(-1px)' : 'translateY(0)', transition: 'transform .14s ease, box-shadow .14s ease', cursor: 'pointer' }) }}
          {...(isWeb ? { onMouseEnter: () => setNewHover(true), onMouseLeave: () => setNewHover(false) } : {})}
        >
          <SkIcon name="plus" size={15} color="#fff" strokeWidth={2.4} />
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>Record payment</Text>
        </Pressable>
      </View>

      {/* Status tabs */}
      <View style={{ flexDirection: 'row', marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', gap: 3, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: 10, padding: 4, flexWrap: 'wrap' }}>
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <Pressable key={t.id} onPress={() => setTab(t.id)} style={{ height: 30, paddingHorizontal: 13, borderRadius: 7, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: active ? colors.card : 'transparent', ...(active ? web({ boxShadow: '0 1px 3px rgba(16,24,40,.10)' }) : {}), ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }) }}>
                <Text style={{ fontFamily: active ? font.sansSemibold : font.sansMedium, fontSize: 12.5, fontWeight: active ? '600' : '500', color: active ? colors.ink : colors.ink3 }}>{t.label}</Text>
                <View style={{ minWidth: 18, height: 17, paddingHorizontal: 5, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? colors.accentSoft : colors.paper3 }}>
                  <Text style={{ fontFamily: font.monoMedium, fontSize: 10, color: active ? colors.accentInk : colors.ink4 }}>{payments.filter(t.f).length}</Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* Table card */}
      <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden', ...theme.shadow('sm') }}>
        {/* Titled header */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 14, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line }}>
          <View style={{ width: 32, height: 32, borderRadius: 10, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 3px 8px -3px ${colors.accent}55` }) }}>
            <SkIcon name="wallet" size={16} color={colors.accentInk} strokeWidth={2} />
          </View>
          <Text style={{ fontFamily: font.sansBold, fontSize: 14.5, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Payments</Text>
          <View style={{ flex: 1 }} />
          {!loading && (
            <View style={{ paddingHorizontal: 9, height: 22, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 11, color: colors.ink3 }}>{payments.length} total</Text>
            </View>
          )}
        </View>

        {loading ? (
          <View style={{ padding: 48, alignItems: 'center' }}><Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3 }}>Loading…</Text></View>
        ) : rows.length === 0 ? (
          <View style={{ paddingVertical: 64, paddingHorizontal: 24, alignItems: 'center' }}>
            <View style={{ width: 96, height: 96, borderRadius: 30, alignItems: 'center', justifyContent: 'center', marginBottom: 20, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, ...web({ backgroundImage: `radial-gradient(circle at 50% 32%, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 14px 38px -10px ${colors.accent}4d` }) }}>
              <View style={{ width: 58, height: 58, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.accentLine, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 6px 16px -6px ${colors.accent}66` }) }}>
                <SkIcon name="wallet" size={26} color={colors.accentInk} strokeWidth={2} />
              </View>
            </View>
            <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 18, color: colors.ink, letterSpacing: -0.3, textAlign: 'center' }}>{query.trim() ? 'No matches found' : 'No payments yet'}</Text>
            <Text style={{ fontFamily: font.sans, fontSize: 13.5, color: colors.ink3, marginTop: 6, textAlign: 'center', maxWidth: 400, lineHeight: 20 }}>{query.trim() ? `No payments match “${query.trim()}”. Try a different word or clear the search.` : 'Record a payment against an open invoice or bill to see it here.'}</Text>
            {!query.trim() && (
              <Pressable onPress={() => setRecordOpen(true)} style={{ marginTop: 20, height: 38, paddingHorizontal: 16, borderRadius: 10, backgroundColor: CTA_A, flexDirection: 'row', alignItems: 'center', gap: 7, ...web({ backgroundImage: `linear-gradient(145deg, ${CTA_A}, ${CTA_B})`, boxShadow: `0 6px 14px -4px ${CTA_B}88`, cursor: 'pointer' }) }}>
                <SkIcon name="plus" size={15} color="#fff" strokeWidth={2.4} />
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>Record payment</Text>
              </Pressable>
            )}
          </View>
        ) : isMobile ? (
          rows.map((p, i) => <PaymentCard key={p.number} p={p} index={i} onOpen={() => setSel(p)} />)
        ) : (
          <ScrollView horizontal={false} showsHorizontalScrollIndicator={false}>
            <View style={{ width: '100%' }}>
              <View style={{ flexDirection: 'row', backgroundColor: colors.paper2, borderBottomWidth: 1, borderColor: colors.line, ...web({ backgroundImage: `linear-gradient(180deg, ${colors.paper2}, ${colors.paper})` }) }}>
                <View style={col('num')}><Text style={th}>Payment #</Text></View>
                <View style={col('contact')}><Text style={th}>Contact</Text></View>
                <View style={col('date', 'center')}><Text style={th}>Date</Text></View>
                <View style={col('mode', 'center')}><Text style={th}>Mode</Text></View>
                <View style={col('ref', 'center')}><Text style={th}>Reference</Text></View>
                <View style={col('amount', 'right')}><Text style={th}>Amount</Text></View>
              </View>
              {rows.map((p, i) => <PaymentRow key={p.number} p={p} index={i} col={col} onOpen={() => setSel(p)} />)}
            </View>
          </ScrollView>
        )}

        {/* Card footer chips */}
        {!loading && (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, paddingHorizontal: 16, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, flexWrap: 'wrap' }}>
            {footChip(`${rows.length} shown`)}
            {footChip(`${advances.length} advances`)}
            {footChip(`${fmtMoney(recIn, cur)} received`, true)}
          </View>
        )}
      </View>

      <PaymentDetailModal visible={sel != null} p={sel} onClose={() => setSel(null)} />

      <RecordPaymentModal visible={recordOpen} onClose={() => setRecordOpen(false)} onDone={() => { setRecordOpen(false); reload(); }} />
    </View>
  );
};

/* ── Record a payment against an open invoice ────────────────────────── */
const MODE_OPTIONS: string[][] = [
  ['BANK_TRANSFER', 'Bank transfer'],
  ['CASH', 'Cash'],
  ['CHEQUE', 'Cheque'],
  ['RAZORPAY', 'Razorpay'],
  ['OTHER', 'Other'],
];

const RecordPaymentModal: React.FC<{ visible: boolean; onClose: () => void; onDone: () => void }> = ({ visible, onClose, onDone }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const [dir, setDir] = useState<'RECEIVED' | 'MADE'>('RECEIVED');
  const [invoices, setInvoices] = useState<BillingDocument[]>([]);
  const [loadingInv, setLoadingInv] = useState(true);
  const [sel, setSel] = useState<BillingDocument | null>(null);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  const isPay = dir === 'MADE'; // pay a vendor bill vs receive against a customer invoice
  const docWord = isPay ? 'bill' : 'invoice';
  const partyWord = isPay ? 'vendor' : 'customer';
  const balanceOf = (d: BillingDocument) => money(d.grand_total) - money((d as any).amount_settled);

  useEffect(() => {
    if (!visible) return;
    setSel(null); setAmount(''); setReference(''); setMode('BANK_TRANSFER'); setBusy(false);
    setDate(new Date().toISOString().slice(0, 10));
    setLoadingInv(true);
    listDocuments({ type: isPay ? 'BILL' : 'INVOICE' })
      .then((list) => {
        const open = (Array.isArray(list) ? list : []).filter(
          (d) =>
            (d.status === 'ISSUED' || d.status === 'PARTIALLY_PAID' || d.status === 'OVERDUE') &&
            balanceOf(d) > 0.0001,
        );
        setInvoices(open);
      })
      .catch(() => setInvoices([]))
      .finally(() => setLoadingInv(false));
  }, [visible, dir]);

  const pick = (d: BillingDocument) => { if (!d.contact_id) return; setSel(d); setAmount(balanceOf(d).toFixed(2)); };

  const submit = async () => {
    if (busy || !sel || !sel.contact_id) return;
    const amt = parseFloat(String(amount).replace(/,/g, '')) || 0;
    if (amt <= 0) return;
    setBusy(true);
    try {
      await createPayment({
        direction: dir,
        contact_id: sel.contact_id || '',
        payment_date: date,
        mode: mode as PaymentMode,
        currency: sel.currency || 'INR',
        amount: amt.toFixed(2),
        reference: reference.trim() || undefined,
        allocations: [{ document_id: sel.id, amount: amt.toFixed(2) }],
      });
      onDone();
    } catch {
      setBusy(false); // error toast shown by the service layer
    }
  };

  const input = { borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 5, height: 36, paddingHorizontal: 11, fontFamily: font.sans, fontSize: 13, color: colors.ink, ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) };
  const label = { fontFamily: font.sans, fontSize: 12, color: colors.ink2, marginBottom: 4, fontWeight: '500' as const };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Record payment"
      subtitle={isPay ? 'Pay a vendor against an open bill' : 'Apply a receipt to an open invoice'}
      width={520}
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 34, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable onPress={submit} disabled={busy || !sel} style={{ height: 34, paddingHorizontal: 16, borderRadius: 5, justifyContent: 'center', backgroundColor: busy || !sel ? colors.paper3 : colors.accent }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: busy || !sel ? colors.ink4 : '#fff' }}>{busy ? 'Recording…' : 'Record payment'}</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 12 }}>
        {/* Receive (from customer) vs Pay (to vendor) */}
        <View style={{ flexDirection: 'row', gap: 2, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: 5, padding: 3, alignSelf: 'flex-start' }}>
          {([['RECEIVED', 'Receive from customer'], ['MADE', 'Pay a vendor']] as const).map(([v, l]) => {
            const a = v === dir;
            return (
              <Pressable key={v} onPress={() => setDir(v)} style={{ height: 28, paddingHorizontal: 12, borderRadius: 3, justifyContent: 'center', backgroundColor: a ? colors.card : 'transparent' }}>
                <Text style={{ fontFamily: font.sans, fontSize: 12, fontWeight: '500', color: a ? colors.ink : colors.ink3 }}>{l}</Text>
              </Pressable>
            );
          })}
        </View>
        <View>
          <Text style={label}>Open {docWord} *</Text>
          {loadingInv ? (
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3, padding: 12 }}>Loading…</Text>
          ) : invoices.length === 0 ? (
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3, padding: 12, borderWidth: 1, borderColor: colors.line, borderRadius: 5 }}>No open {docWord}s. Issue a {docWord} first.</Text>
          ) : (
            <ScrollView style={{ maxHeight: 190, borderWidth: 1, borderColor: colors.line, borderRadius: 5 }}>
              {invoices.map((d) => {
                const active = sel?.id === d.id;
                const noCustomer = !d.contact_id;
                return (
                  <Pressable key={d.id} onPress={() => pick(d)} disabled={noCustomer} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderColor: colors.line, backgroundColor: active ? colors.accentSoft : colors.card, opacity: noCustomer ? 0.55 : 1 }}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text numberOfLines={1} style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: active ? colors.accentInk : colors.ink }}>{d.doc_number || 'Draft'} · {(d as any).contact_name || `No ${partyWord}`}</Text>
                      <Text style={{ fontFamily: font.sans, fontSize: 11, color: noCustomer ? colors.warn : colors.ink4 }}>
                        {noCustomer ? `No ${partyWord} on this ${docWord} — can’t record a payment` : `Balance due ${fmtMoney(balanceOf(d), cur)}${d.status === 'PARTIALLY_PAID' ? ' · part-paid' : ''}`}
                      </Text>
                    </View>
                    {active && <SkIcon name="check" size={15} color={colors.accentInk} strokeWidth={2.4} />}
                  </Pressable>
                );
              })}
            </ScrollView>
          )}
        </View>

        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={label}>Amount *</Text>
            <TextInput value={amount} onChangeText={setAmount} keyboardType="decimal-pad" placeholder="0.00" placeholderTextColor={colors.ink4} style={input} />
            {sel && <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, marginTop: 4 }}>Due {fmtMoney(balanceOf(sel), cur)} — edit for a partial payment</Text>}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={label}>Date</Text>
            <DateField value={date} onChange={setDate} />
          </View>
        </View>

        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={label}>Mode</Text>
            <DropdownSelect value={mode} options={MODE_OPTIONS} onChange={setMode} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={label}>Reference (optional)</Text>
            <TextInput value={reference} onChangeText={setReference} placeholder="UTR / cheque no." placeholderTextColor={colors.ink4} style={input} />
          </View>
        </View>
      </View>
    </ModalShell>
  );
};

export default Payments;
